import React from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { WalletIcon, ChevronRightIcon, CalendarIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../../app/providers/CompanyProvider';
export function BalanceSnapshotWidget() {
  const {
    t
  } = useTranslation('analytics');
  const navigate = useNavigate();
  const {
    selectedCompany
  } = useCompany();
  // Per-company balance data
  const allBalanceData: Record<string, {
    availableBalance: number;
    upcomingPayout: number;
  }> = {
    'master-001': {
      availableBalance: 61767.25,
      upcomingPayout: 45230.8
    },
    'submerchant-kl-001': {
      availableBalance: 12500.0,
      upcomingPayout: 9000.0
    },
    'submerchant-kl-002': {
      availableBalance: 10800.0,
      upcomingPayout: 7800.0
    },
    'submerchant-kl-003': {
      availableBalance: 9200.0,
      upcomingPayout: 6500.0
    },
    'submerchant-kl-004': {
      availableBalance: 11400.0,
      upcomingPayout: 8200.0
    },
    'submerchant-kl-005': {
      availableBalance: 8900.0,
      upcomingPayout: 6400.0
    },
    'submerchant-kl-006': {
      availableBalance: 8967.25,
      upcomingPayout: 7330.8
    }
  };
  // Filter balance based on selected company
  const getBalanceForCompany = () => {
    if (selectedCompany.type === 'master') {
      // Master merchant: sum all sub-merchants
      const subMerchantIds = Object.keys(allBalanceData).filter(id => id.startsWith('submerchant-'));
      const totalAvailable = subMerchantIds.reduce((sum, id) => sum + (allBalanceData[id]?.availableBalance || 0), 0);
      const totalPayout = subMerchantIds.reduce((sum, id) => sum + (allBalanceData[id]?.upcomingPayout || 0), 0);
      return {
        availableBalance: totalAvailable,
        upcomingPayout: totalPayout
      };
    } else {
      // Specific sub-merchant
      return allBalanceData[selectedCompany.id] || {
        availableBalance: 0,
        upcomingPayout: 0
      };
    }
  };
  const balanceData = {
    ...getBalanceForCompany(),
    currency: getTenant().currency.code,
    lastUpdated: '5 May 2026 at 1:38 am MVT',
    payoutDate: '6 May 2026'
  };
  const formatCurrency = (amount: number) => {
    return formatCurrencyAmount(amount, {
      fractionDigits: 2,
      currency: balanceData.currency
    });
  };
  return <div onClick={() => navigate('/balance')} className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:border-gray-300 transition-colors flex flex-col h-full" data-id="element-1459">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-white" data-id="element-1460">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-1461">
          <WalletIcon size={16} className="mr-2 text-green-600" data-id="element-1462" />
          {t('components.BalanceSnapshotWidget.available_balance')}
        </h3>
        <button onClick={e => {
        e.stopPropagation();
        navigate('/balance');
      }} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center" data-id="element-1463">
          {t('components.BalanceSnapshotWidget.view_all')}
          <ChevronRightIcon size={14} className="ml-1" data-id="element-1464" />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 py-3 bg-white flex-1" data-id="element-1465">
        <p className="text-xs text-gray-500 mb-1" data-id="element-1466">
          {t('components.BalanceSnapshotWidget.updated_on')}{' '}
          {balanceData.lastUpdated}
        </p>
        <div className="text-2xl font-bold text-gray-900" data-id="element-1467">
          {formatCurrency(balanceData.availableBalance)}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#FEE7E5] px-4 py-3 rounded-b-lg flex items-center justify-between h-12" data-id="element-1468">
        <div className="flex items-center gap-1.5 min-w-0" data-id="element-1469">
          <CalendarIcon size={14} className="text-[#9F140F] flex-shrink-0" aria-hidden="true" data-id="element-1470" />
          <span className="text-xs font-medium text-[#5C0C08] truncate" data-id="element-1471">
            {t('components.BalanceSnapshotWidget.upcoming_payout')}{' '}
            {formatCurrency(balanceData.upcomingPayout)} ·{' '}
            {balanceData.payoutDate}
          </span>
        </div>
        <button onClick={e => {
        e.stopPropagation();
        navigate('/payouts');
      }} className="text-xs text-[#9F140F] hover:text-[#5C0C08] font-semibold flex items-center flex-shrink-0 ml-3" data-id="element-1472">
          {t('components.BalanceSnapshotWidget.view_payout_details')}
          <ChevronRightIcon size={14} className="ml-1" data-id="element-1473" />
        </button>
      </div>
    </div>;
}